import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// create a small custom marker icon
const createSmallIcon = () => {
  return L.divIcon({
    className: "custom-div-icon",
    html: '<div style="background-color: #ef4444; width: 8px; height: 8px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

// fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  Edit,
  Trash2,
  Plus,
  Star,
  StarOff,
  MapPin,
  Home,
  Calendar,
  Copy,
  User,
  Eye,
  MoreHorizontal,
  FileText,
  EyeOff,
  Tag,
  KeyRound,
} from "lucide-react";
import { PropertyFormWrapper } from "./property-form-wrapper";
import {
  PropertyDetailsDialog,
  type CommonTypeFields,
} from "./property-details-dialog";
import { PropertyImageThumbnail } from "@/components/ui/property-image-thumbnail";
import type { Id } from "../../convex/_generated/dataModel";
import { ChevronUp, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download } from "lucide-react";
import type { Tag as PropertyTag } from "../../convex/types";
import {
  RENTED_TAG_NAME,
  SOLD_TAG_NAME,
  getStatusTags,
  resolvePropertyTags,
} from "@/lib/property-tags";
import {
  clearDashboardPropertyFilter,
  countActiveDashboardPropertyFilters,
  getDashboardPropertyFilterChips,
  hasAnyDashboardPropertyFilter,
  uniqueNonEmptyStrings,
} from "./property-table/filters";
import { usePropertyTableFilters } from "./property-table/use-property-table-filters";
import { PropertyTableFilters } from "./property-table/property-table-filters";

type StatusTagKind = "sold" | "rented";

const STATUS_TAG_NAMES: Record<
  StatusTagKind,
  { tagName: string; oppositeTagName: string }
> = {
  sold: { tagName: SOLD_TAG_NAME, oppositeTagName: RENTED_TAG_NAME },
  rented: { tagName: RENTED_TAG_NAME, oppositeTagName: SOLD_TAG_NAME },
};

/**
 * Only rental listings can be marked as rented.
 */
function isRentalListing(propertyTypes: unknown): boolean {
  return Array.isArray(propertyTypes) && propertyTypes.includes("rentals");
}

interface PropertyTableProps {
  preloadedProperties?: any[];
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
type SortField = "reff_id" | "created_at" | "property_name" | null;
type SortOrder = "asc" | "desc";

function readInitialPage(): number {
  if (typeof window === "undefined") {
    return 1;
  }

  const rawValue = new URLSearchParams(window.location.search).get("page");
  const parsedValue = rawValue ? Number(rawValue) : 1;
  if (!Number.isFinite(parsedValue) || parsedValue < 1) {
    return 1;
  }

  return Math.floor(parsedValue);
}

function readInitialPageSize(): number {
  if (typeof window === "undefined") {
    return 25;
  }

  const rawValue = new URLSearchParams(window.location.search).get("pageSize");
  const parsedValue = rawValue ? Number(rawValue) : 25;
  if (
    !Number.isFinite(parsedValue) ||
    !PAGE_SIZE_OPTIONS.includes(parsedValue)
  ) {
    return 25;
  }

  return parsedValue;
}

function readInitialSortField(): SortField {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = new URLSearchParams(window.location.search).get("sort");
  if (
    rawValue === "reff_id" ||
    rawValue === "created_at" ||
    rawValue === "property_name"
  ) {
    return rawValue;
  }

  return null;
}

function readInitialSortOrder(): SortOrder {
  if (typeof window === "undefined") {
    return "asc";
  }

  const rawValue = new URLSearchParams(window.location.search).get("order");
  if (rawValue === "desc") {
    return "desc";
  }

  return "asc";
}

function readInitialFiltersOpen(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("filtersOpen") === "1";
}

function syncPropertyTableQueryState(
  currentPage: number,
  pageSize: number,
  sortField: SortField,
  sortOrder: SortOrder,
  moreFiltersOpen: boolean,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  params.delete("page");
  params.delete("pageSize");
  params.delete("sort");
  params.delete("order");
  params.delete("filtersOpen");

  if (currentPage > 1) {
    params.set("page", currentPage.toString());
  }

  if (pageSize !== 25) {
    params.set("pageSize", pageSize.toString());
  }

  if (sortField) {
    params.set("sort", sortField);
    params.set("order", sortOrder);
  }

  if (moreFiltersOpen) {
    params.set("filtersOpen", "1");
  }

  const queryString = params.toString();
  const nextUrl =
    queryString === ""
      ? window.location.pathname
      : `${window.location.pathname}?${queryString}`;
  const currentUrl = `${window.location.pathname}${window.location.search}`;

  if (nextUrl !== currentUrl) {
    window.history.replaceState(window.history.state, "", nextUrl);
  }
}

// small map component for table cells
function SmallMap({
  lat,
  lng,
  propertyName,
  onClick,
}: {
  lat: number;
  lng: number;
  propertyName?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="w-14 h-10 rounded border overflow-hidden cursor-pointer hover:shadow-md transition-shadow relative z-0"
      onClick={onClick}
      title="Click to view larger map"
    >
      <MapContainer
        center={[lat, lng] as [number, number]}
        zoom={13}
        style={{ width: "100%", height: "100%", zIndex: 0 }}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <Marker
          position={[lat, lng] as [number, number]}
          icon={createSmallIcon()}
        />
      </MapContainer>
    </div>
  );
}

export function PropertyTable({ preloadedProperties }: PropertyTableProps) {
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<
    "view" | "edit" | "create" | null
  >(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewProperty, setViewProperty] = useState<any>(null);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [selectedMapProperty, setSelectedMapProperty] = useState<any>(null);
  const [sortField, setSortField] = useState<SortField>(() =>
    readInitialSortField(),
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(() =>
    readInitialSortOrder(),
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const [pendingStatusTagPropertyId, setPendingStatusTagPropertyId] =
    useState<Id<"base_properties"> | null>(null);
  const [confettiSource, setConfettiSource] = useState({
    x: 0,
    y: 0,
    w: 100,
    h: 0,
  });
  const { width, height } = useWindowSize();
  const { filters, appliedFilters, setFilters, resetFilters } =
    usePropertyTableFilters();
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(() =>
    readInitialFiltersOpen(),
  );

  // pagination state
  const [currentPage, setCurrentPage] = useState(() => readInitialPage());
  const [pageSize, setPageSize] = useState(() => readInitialPageSize());

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, sortField, sortOrder]);

  useEffect(() => {
    syncPropertyTableQueryState(
      currentPage,
      pageSize,
      sortField,
      sortOrder,
      moreFiltersOpen,
    );
  }, [currentPage, moreFiltersOpen, pageSize, sortField, sortOrder]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(readInitialPage());
      setPageSize(readInitialPageSize());
      setSortField(readInitialSortField());
      setSortOrder(readInitialSortOrder());
      setMoreFiltersOpen(readInitialFiltersOpen());
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // get current cursor for the page
  const currentCursor =
    currentPage <= 1 ? null : ((currentPage - 1) * pageSize).toString();
  const queryFilters = useMemo(() => {
    const nextFilters: {
      search_query?: string;
      tag_ids?: Id<"tag">[];
      property_types?: string[];
      status?: string;
      bedroom_min?: number;
      bedroom_max?: number;
      bathroom_min?: number;
      bathroom_max?: number;
      build_size_min?: number;
      build_size_max?: number;
      land_size_min?: number;
      land_size_max?: number;
      year_built_min?: number;
      year_built_max?: number;
      price_min?: number;
      price_max?: number;
      has_video_thumbnail?: boolean;
      visibility?: "published" | "hidden" | "draft";
      listing_agent_keys?: string[];
    } = {};

    if (appliedFilters.searchQuery.trim() !== "") {
      nextFilters.search_query = appliedFilters.searchQuery.trim();
    }
    if (appliedFilters.tagIds.length > 0) {
      nextFilters.tag_ids = appliedFilters.tagIds;
    }
    if (appliedFilters.propertyTypes.length > 0) {
      nextFilters.property_types = appliedFilters.propertyTypes;
    }
    if (appliedFilters.status !== "") {
      nextFilters.status = appliedFilters.status;
    }
    if (appliedFilters.bedroomMin !== null) {
      nextFilters.bedroom_min = appliedFilters.bedroomMin;
    }
    if (appliedFilters.bedroomMax !== null) {
      nextFilters.bedroom_max = appliedFilters.bedroomMax;
    }
    if (appliedFilters.bathroomMin !== null) {
      nextFilters.bathroom_min = appliedFilters.bathroomMin;
    }
    if (appliedFilters.bathroomMax !== null) {
      nextFilters.bathroom_max = appliedFilters.bathroomMax;
    }
    if (appliedFilters.buildSizeMin !== null) {
      nextFilters.build_size_min = appliedFilters.buildSizeMin;
    }
    if (appliedFilters.buildSizeMax !== null) {
      nextFilters.build_size_max = appliedFilters.buildSizeMax;
    }
    if (appliedFilters.landSizeMin !== null) {
      nextFilters.land_size_min = appliedFilters.landSizeMin;
    }
    if (appliedFilters.landSizeMax !== null) {
      nextFilters.land_size_max = appliedFilters.landSizeMax;
    }
    if (appliedFilters.yearBuiltMin !== null) {
      nextFilters.year_built_min = appliedFilters.yearBuiltMin;
    }
    if (appliedFilters.yearBuiltMax !== null) {
      nextFilters.year_built_max = appliedFilters.yearBuiltMax;
    }
    if (appliedFilters.priceMin !== null) {
      nextFilters.price_min = appliedFilters.priceMin;
    }
    if (appliedFilters.priceMax !== null) {
      nextFilters.price_max = appliedFilters.priceMax;
    }
    if (appliedFilters.hasVideoThumbnail === "withVideo") {
      nextFilters.has_video_thumbnail = true;
    }
    if (appliedFilters.hasVideoThumbnail === "withoutVideo") {
      nextFilters.has_video_thumbnail = false;
    }
    if (appliedFilters.visibility !== "all") {
      nextFilters.visibility = appliedFilters.visibility;
    }
    if (appliedFilters.listingAgentKeys.length > 0) {
      nextFilters.listing_agent_keys = uniqueNonEmptyStrings(
        appliedFilters.listingAgentKeys,
      );
    }

    return nextFilters;
  }, [appliedFilters]);

  // always call all hooks - no conditional hooks
  const propertyResults = useQuery(
    api.properties.getAllBasePropertiesWithTypes,
    {
      paginationOpts: { numItems: pageSize, cursor: currentCursor },
      filters: queryFilters,
      sorting: sortField ? { field: sortField, order: sortOrder } : undefined,
    },
  );

  const propertyStats = useQuery(api.properties.getPropertyStats, {});
  const listingAgentsByProperty = useQuery(
    api.properties.getPropertiesByListingAgent,
    {},
  );
  const tags = useQuery(api.tags.getAllTags, {});
  const currentUser = useQuery(api.users.current);

  // mutations
  const deleteProperty = useMutation(api.properties.deleteBaseProperty);
  const toggleFeatured = useMutation(api.properties.toggleFeatured);
  const duplicateProperty = useMutation(api.properties.duplicateBaseProperty);
  const toggleDraft = useMutation(api.properties.toggleDraft);
  const toggleHidden = useMutation(api.properties.toggleHidden);
  const toggleSoldTag = useMutation(api.properties.toggleSoldTag);
  const toggleRentedTag = useMutation(api.properties.toggleRentedTag);

  // actions for subtitle generation
  const generateSubtitle = useAction(api.properties.generatePropertySubtitle);

  // derive data from queries and handle cursor updates
  const properties = useMemo(() => {
    if (preloadedProperties) return preloadedProperties;

    if (
      propertyResults &&
      typeof propertyResults === "object" &&
      "page" in propertyResults
    ) {
      return (propertyResults as any).page || [];
    }
    return [];
  }, [preloadedProperties, propertyResults]);

  const hasNextPage = useMemo(() => {
    if (
      propertyResults &&
      typeof propertyResults === "object" &&
      "isDone" in propertyResults
    ) {
      return !(propertyResults as any).isDone;
    }
    return false;
  }, [propertyResults]);

  const paginatedProperties = useMemo(() => properties, [properties]);
  const totalCount = useMemo(() => {
    if (preloadedProperties) {
      return preloadedProperties.length;
    }

    if (
      propertyResults &&
      typeof propertyResults === "object" &&
      "totalCount" in propertyResults &&
      typeof (propertyResults as any).totalCount === "number"
    ) {
      return (propertyResults as any).totalCount as number;
    }

    return properties.length;
  }, [preloadedProperties, properties.length, propertyResults]);

  const tagLabelMap = useMemo(() => {
    const nextMap = new Map<string, string>();
    if (!tags) {
      return nextMap;
    }

    for (const tag of tags as PropertyTag[]) {
      nextMap.set(tag._id, tag.tag_name);
    }

    return nextMap;
  }, [tags]);

  const listingAgentLabelMap = useMemo(() => {
    const nextMap = new Map<string, string>();
    if (!listingAgentsByProperty) {
      return nextMap;
    }
    const formatter = new Intl.NumberFormat();
    for (const agent of listingAgentsByProperty) {
      const label = `${agent.agentName} · ${formatter.format(agent.propertyCount)} properties`;
      nextMap.set(agent.agentId, label);
    }
    return nextMap;
  }, [listingAgentsByProperty]);

  const activeFilterCount = useMemo(() => {
    return countActiveDashboardPropertyFilters(filters);
  }, [filters]);

  const filterChips = useMemo(() => {
    return getDashboardPropertyFilterChips(
      filters,
      tagLabelMap,
      listingAgentLabelMap,
    );
  }, [filters, tagLabelMap, listingAgentLabelMap]);

  const hasActiveFilters = useMemo(() => {
    return hasAnyDashboardPropertyFilter(filters);
  }, [filters]);

  // calculate total pages and pagination info
  const paginationInfo = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const showingStart =
      totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const showingEnd =
      totalCount === 0 ? 0 : Math.min(currentPage * pageSize, totalCount);

    return {
      totalPages,
      totalCount,
      showingStart,
      showingEnd,
      canGoPrevious: currentPage > 1,
      canGoNext: hasNextPage || currentPage < totalPages,
    };
  }, [currentPage, hasNextPage, pageSize, totalCount]);

  // simple format functions
  const formatPropertyType = (propertyType: string | null | undefined) => {
    if (!propertyType) return "N/A";
    return propertyType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(timestamp));
  };

  const formatCreator = (creator?: string) => {
    if (!creator) return "Unknown";

    // handle email addresses - extract name part before @
    if (creator.includes("@")) {
      const namePart = creator.split("@")[0];
      // convert underscores/dots to spaces and capitalize
      return namePart
        .replace(/[._]/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    // handle special cases
    if (creator === "website_form") return "Website Form";
    if (creator === "admin") return "Admin";
    if (creator === "system") return "System";

    // default: capitalize first letter
    return creator.charAt(0).toUpperCase() + creator.slice(1);
  };

  // pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(Number(newPageSize));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    resetFilters();
    setMoreFiltersOpen(false);
  };

  const handleRemoveFilterChip = (chipKey: string) => {
    const nextFilters = clearDashboardPropertyFilter(filters, chipKey);
    setFilters(nextFilters);
  };

  const handlePrevious = () => {
    if (paginationInfo.canGoPrevious) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (paginationInfo.canGoNext) {
      handlePageChange(currentPage + 1);
    }
  };

  // generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const totalPages = paginationInfo.totalPages;

    const maxPages = Math.min(maxVisiblePages, totalPages);
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + maxPages - 1);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("ellipsis");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("ellipsis");
      pages.push(totalPages);
    }

    return pages;
  };

  // other handlers
  const handleEdit = (property: any) => {
    setSelectedProperty(property);
    setDialogMode("edit");
  };

  const handleCreate = () => {
    setSelectedProperty(null);
    setDialogMode("create");
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedProperty(null);
  };

  const handleViewDetails = (property: any) => {
    setViewProperty(property);
    setViewDetailsOpen(true);
  };

  const handleMapClick = (property: any) => {
    setSelectedMapProperty(property);
    setMapDialogOpen(true);
  };

  const handleDelete = async (id: Id<"base_properties">) => {
    if (confirm("Are you sure you want to delete this property?")) {
      try {
        await deleteProperty({ id, deleted_by: "admin" });
        toast.success("Property deleted successfully");
      } catch (error) {
        console.error("Failed to delete property:", error);
        toast.error("Failed to delete property");
      }
    }
  };

  const handleToggleFeatured = async (
    id: Id<"base_properties">,
    currentFeatured: boolean,
  ) => {
    try {
      await toggleFeatured({
        id,
        featured: !currentFeatured,
        updated_by: currentUser?.email || "system",
      });
      toast.success("Featured status updated");
    } catch (error) {
      console.error("Failed to toggle featured status:", error);
      toast.error("Failed to update featured status");
    }
  };

  const handleDuplicate = async (
    id: Id<"base_properties">,
    originalSlug: string,
  ) => {
    try {
      const newSlug = `${originalSlug}-copy-${Date.now()}`;
      await duplicateProperty({
        id,
        new_slug: newSlug,
        created_by: currentUser?.email || "system",
      });
      toast.success("Property duplicated successfully");
    } catch (error) {
      console.error("Failed to duplicate property:", error);
      toast.error("Failed to duplicate property");
    }
  };

  const handleToggleDraft = async (
    id: Id<"base_properties">,
    currentDraft: boolean,
  ) => {
    try {
      await toggleDraft({
        id,
        draft: !currentDraft,
        updated_by: currentUser?.email || "system",
      });
      toast.success(
        currentDraft ? "Property published" : "Property moved to draft",
      );
    } catch (error) {
      console.error("Failed to toggle draft status:", error);
      toast.error("Failed to update property status");
    }
  };

  const handleToggleHidden = async (
    id: Id<"base_properties">,
    currentHidden: boolean,
  ) => {
    try {
      await toggleHidden({
        id,
        hidden: !currentHidden,
        updated_by: currentUser?.email || "system",
      });
      toast.success(currentHidden ? "Property unhidden" : "Property hidden");
    } catch (error) {
      console.error("Failed to toggle hidden status:", error);
      toast.error("Failed to update property visibility");
    }
  };

  /**
   * Adds or removes the SOLD! or Rented tag. The server swaps out the other
   * status tag when adding one, since a listing can't be sold and rented.
   */
  const handleToggleStatusTag = (
    kind: StatusTagKind,
    listing: { id: Id<"base_properties">; name: string; hasTag: boolean },
    event: React.MouseEvent,
  ) => {
    // one status change at a time so a double click can't undo itself
    if (pendingStatusTagPropertyId !== null) {
      return;
    }

    const { tagName, oppositeTagName } = STATUS_TAG_NAMES[kind];
    const toggle = kind === "sold" ? toggleSoldTag : toggleRentedTag;

    // show confetti immediately when adding the tag (before server call)
    if (!listing.hasTag) {
      const rect = event.currentTarget.getBoundingClientRect();
      setConfettiSource({
        x: rect.left + rect.width / 2 - 50, // center the confetti source
        y: rect.top + rect.height / 2,
        w: 100,
        h: 0,
      });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000); // hide after 2 seconds
    }

    setPendingStatusTagPropertyId(listing.id);
    void toggle({
      id: listing.id,
      updated_by:
        currentUser && currentUser.email ? currentUser.email : "system",
    })
      .then((result) => {
        if (!result.ok) {
          setShowConfetti(false);
          toast.error(result.error.message);
          return;
        }

        if (!result.data.hasTag) {
          toast.success(`${tagName} tag removed from ${listing.name}.`);
          return;
        }

        if (result.data.removedOppositeTag) {
          toast.success(
            `${tagName} tag added to ${listing.name}, replacing ${oppositeTagName} 🎉`,
          );
          return;
        }

        toast.success(`${tagName} tag added to ${listing.name} 🎉`);
      })
      .then(undefined, (error: unknown) => {
        console.error(`Failed to toggle ${tagName} tag:`, error);
        setShowConfetti(false);
        toast.error(
          `The ${tagName} tag on ${listing.name} didn't update. Check your connection and try again.`,
        );
      })
      .finally(() => {
        setPendingStatusTagPropertyId(null);
      });
  };

  const handleGenerateSubtitle = async (id: Id<"base_properties">) => {
    try {
      const loadingToastId = toast.loading("Generating subtitle...");
      await generateSubtitle({ propertyId: id });
      toast.success("Subtitle generated successfully!", { id: loadingToastId });
    } catch (error) {
      console.error("Failed to generate subtitle:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to generate subtitle",
      );
    }
  };

  const handleDownloadImages = async (
    id: Id<"base_properties">,
    reffId?: string,
  ) => {
    console.log("handleDownloadImages", { id, reffId });

    const endpoint = `/api/properties/${encodeURIComponent(id)}/download-images`;
    const responseResult = await fetch(endpoint, { method: "GET" }).then(
      (response) => ({ ok: true as const, response }),
      (error: unknown) => ({
        ok: false as const,
        error: { type: "request_failed" as const, cause: error },
      }),
    );

    if (responseResult.ok === false) {
      console.error("download images request failed", responseResult.error);
      toast.error(
        "Images could not be downloaded; check the download service and try again.",
      );
      return;
    }

    const res = responseResult.response;
    if (!res.ok) {
      console.error("download images endpoint failed", {
        status: res.status,
        statusText: res.statusText,
      });
      toast.error(
        "Images could not be downloaded; check the download service and try again.",
      );
      return;
    }

    const blobResult = await res.blob().then(
      (blob) => ({ ok: true as const, blob }),
      (error: unknown) => ({
        ok: false as const,
        error: { type: "blob_read_failed" as const, cause: error },
      }),
    );

    if (blobResult.ok === false) {
      console.error("download images blob read failed", blobResult.error);
      toast.error(
        "Images could not be downloaded; check the download service and try again.",
      );
      return;
    }

    const url = URL.createObjectURL(blobResult.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reffId || "property"}-images.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSortToggle = () => {
    if (sortField === "reff_id") {
      if (sortOrder === "asc") {
        // first click: asc -> desc
        setSortOrder("desc");
      } else {
        // second click: desc -> clear sorting
        setSortField(null);
        setSortOrder("asc");
      }
    } else {
      // no sorting active: set to reff_id sorting with asc order
      setSortField("reff_id");
      setSortOrder("asc");
    }
  };

  const isLoading = !preloadedProperties && !propertyResults;

  // fetch detailed data for view dialog to ensure we have all property types
  const detailedArgs =
    viewDetailsOpen &&
    viewProperty &&
    viewProperty.baseProperty &&
    viewProperty.baseProperty._id
      ? { id: viewProperty.baseProperty._id as Id<"base_properties"> }
      : ("skip" as const);
  const detailedQuery = useQuery(
    api.properties.getPropertyWithRelations,
    detailedArgs !== "skip"
      ? { ...detailedArgs, showDrafts: true, showHidden: true }
      : "skip",
  );

  const typeDataMap = useMemo(() => {
    if (
      detailedQuery &&
      typeof detailedQuery === "object" &&
      (detailedQuery as any).specificPropertyDataMap
    ) {
      return (detailedQuery as any).specificPropertyDataMap as Record<
        string,
        CommonTypeFields
      >;
    }
    return viewProperty && viewProperty.specificPropertyDataMap
      ? (viewProperty.specificPropertyDataMap as Record<
          string,
          CommonTypeFields
        >)
      : undefined;
  }, [detailedQuery, viewProperty]);

  const singleTypeData = useMemo((): CommonTypeFields | undefined => {
    if (
      detailedQuery &&
      typeof detailedQuery === "object" &&
      (detailedQuery as any).specificPropertyData
    ) {
      return (detailedQuery as any).specificPropertyData as CommonTypeFields;
    }
    return viewProperty
      ? (viewProperty.specificPropertyData as CommonTypeFields | undefined as
          | CommonTypeFields
          | undefined)
      : undefined;
  }, [detailedQuery, viewProperty]);

  const primaryType = useMemo(() => {
    if (
      detailedQuery &&
      typeof detailedQuery === "object" &&
      (detailedQuery as any).propertyType
    ) {
      return (detailedQuery as any).propertyType as string | null;
    }
    return viewProperty ? (viewProperty.propertyType as string | null) : null;
  }, [detailedQuery, viewProperty]);
  const isDetailedQueryLoading =
    detailedArgs !== "skip" && detailedQuery === undefined;

  const propertyTypesForView = useMemo(() => {
    if (
      detailedQuery &&
      typeof detailedQuery === "object" &&
      "propertyTypes" in detailedQuery &&
      Array.isArray(
        (detailedQuery as { propertyTypes?: string[] }).propertyTypes,
      )
    ) {
      return (detailedQuery as { propertyTypes: string[] }).propertyTypes;
    }

    if (viewProperty && Array.isArray(viewProperty.propertyTypes)) {
      return viewProperty.propertyTypes as string[];
    }

    if (primaryType) {
      return [primaryType];
    }

    return [] as string[];
  }, [detailedQuery, primaryType, viewProperty]);

  const hasInventory = propertyStats ? propertyStats.total > 0 : totalCount > 0;
  const emptyStateHasFilters = hasActiveFilters;

  return (
    <div className="space-y-3 w-full">
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={150}
          recycle={false}
          run={showConfetti}
          colors={[
            "#FFD700", // gold
            "#FFA500", // orange
            "#FF6347", // tomato
            "#32CD32", // lime green
            "#1E90FF", // dodger blue
            "#FF69B4", // hot pink
            "#9370DB", // medium purple
            "#FF1493", // deep pink
            "#00CED1", // dark turquoise
            "#FF8C00", // dark orange
          ]}
          gravity={0.8}
          initialVelocityY={30}
          initialVelocityX={15}
          tweenDuration={10}
          confettiSource={confettiSource}
        />
      )}
      <Card className="w-full">
        <CardContent className="space-y-2 px-3 pb-3 pt-3">
          <PropertyTableFilters
            filters={filters}
            activeFilterCount={activeFilterCount}
            filterChips={filterChips}
            moreFiltersOpen={moreFiltersOpen}
            tags={tags || []}
            listingAgents={listingAgentsByProperty}
            onFiltersChange={setFilters}
            onClearFilters={handleClearFilters}
            onRemoveChip={handleRemoveFilterChip}
            onMoreFiltersOpenChange={setMoreFiltersOpen}
            headerAction={
              <Button
                onClick={handleCreate}
                size="sm"
                className="h-8 gap-1 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Add property</span>
              </Button>
            }
          />

          {/* stats */}
          <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
            <div className="text-center p-1 bg-muted/50 rounded-md">
              <div className="text-sm font-bold tabular-nums text-primary">
                {propertyStats?.total || 0}
              </div>
              <div className="text-[11px] text-muted-foreground">Total</div>
            </div>
            <div className="text-center p-1 bg-muted/50 rounded-md">
              <div className="text-sm font-bold tabular-nums text-yellow-600">
                {propertyStats?.featured || 0}
              </div>
              <div className="text-[11px] text-muted-foreground">Featured</div>
            </div>
            <div className="text-center p-1 bg-muted/50 rounded-md">
              <div className="text-sm font-bold tabular-nums text-green-600">
                {propertyStats?.published || 0}
              </div>
              <div className="text-[11px] text-muted-foreground">Published</div>
            </div>
            <div className="text-center p-1 bg-muted/50 rounded-md">
              <div className="text-sm font-bold tabular-nums text-orange-600">
                {propertyStats?.drafts || 0}
              </div>
              <div className="text-[11px] text-muted-foreground">Drafts</div>
            </div>
          </div>

          {/* table controls */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Show</span>
              <Select
                value={pageSize.toString()}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-16 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">entries</span>
            </div>

            <div className="text-xs text-muted-foreground">
              {paginationInfo.totalCount === 0
                ? "No properties to show"
                : `Showing ${paginationInfo.showingStart}-${paginationInfo.showingEnd} of ${paginationInfo.totalCount} ${
                    hasActiveFilters ? "filtered properties" : "properties"
                  }`}
              {sortField === "reff_id" && (
                <span className="ml-2 text-primary">
                  • Sorted by Reference ID (
                  {sortOrder === "asc" ? "Low to High" : "High to Low"})
                </span>
              )}
            </div>
          </div>

          {/* loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-xs text-muted-foreground">
                Updating property results.
              </div>
            </div>
          ) : (
            /* table */
            <div className="rounded-md border w-full overflow-y-auto max-h-[calc(100vh-300px)]">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 select-none transition-colors"
                      onClick={handleSortToggle}
                      title="Click to sort by Reference ID (numeric)"
                    >
                      <div className="flex items-center gap-1">
                        Property
                        {sortField === "reff_id" ? (
                          sortOrder === "asc" ? (
                            <ChevronUp className="h-3 w-3 text-primary" />
                          ) : (
                            <ChevronDown className="h-3 w-3 text-primary" />
                          )
                        ) : (
                          <div className="h-3 w-3 flex flex-col items-center justify-center opacity-50">
                            <ChevronUp className="h-1.5 w-1.5 -mb-0.5" />
                            <ChevronDown className="h-1.5 w-1.5" />
                          </div>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Images</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedProperties.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-6">
                        <div className="flex flex-col items-center gap-1.5">
                          <Home className="h-6 w-6 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            {emptyStateHasFilters
                              ? "No properties match these filters. Clear a filter or broaden your search."
                              : hasInventory
                                ? "No properties are showing right now. Try refreshing the results."
                                : "No properties yet. Add your first property to get started."}
                          </p>
                          {emptyStateHasFilters ? (
                            <Button
                              onClick={handleClearFilters}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              Clear filters
                            </Button>
                          ) : (
                            <Button
                              onClick={handleCreate}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              <Plus className="h-3.5 w-3.5 mr-1.5" />
                              Add your first property
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedProperties.map((property: any) => (
                      <TableRow key={property.baseProperty?._id}>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="font-medium text-xs">
                              {property.baseProperty?.property_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {property.baseProperty?.reff_id}
                            </div>
                            {property.baseProperty?.tags &&
                              property.baseProperty.tags.length > 0 && (
                                <div className="flex flex-wrap gap-0.5 mt-0.5">
                                  {(() => {
                                    const propertyTags = resolvePropertyTags(
                                      property.baseProperty.tags,
                                      tags,
                                    );

                                    // a SOLD! or Rented listing shows only that tag, otherwise show up to 3 tags
                                    const statusTags =
                                      getStatusTags(propertyTags);
                                    const hasStatusTag = statusTags.length > 0;
                                    const tagsToShow = hasStatusTag
                                      ? statusTags
                                      : propertyTags.slice(0, 3);

                                    return (
                                      <>
                                        {tagsToShow.map((tag) => (
                                          <Badge
                                            key={tag._id}
                                            variant={
                                              hasStatusTag
                                                ? "destructive"
                                                : "outline"
                                            }
                                            className={`text-xs ${
                                              hasStatusTag
                                                ? "font-semibold"
                                                : ""
                                            }`}
                                          >
                                            {tag.tag_name}
                                          </Badge>
                                        ))}
                                        {!hasStatusTag &&
                                          propertyTags.length > 3 && (
                                            <Badge
                                              variant="outline"
                                              className="text-xs"
                                            >
                                              +{propertyTags.length - 3}
                                            </Badge>
                                          )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <PropertyImageThumbnail
                            images={property.baseProperty?.images || []}
                            size="md"
                            showCount={true}
                            maxImages={1}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              // get property types from the property data
                              const propertyTypes =
                                property.propertyTypes || [];
                              const hasMultipleTypes = propertyTypes.length > 1;
                              const firstType =
                                propertyTypes[0] || property.propertyType;

                              if (!firstType) return null;

                              return (
                                <Badge variant="outline" className="text-xs">
                                  {formatPropertyType(firstType)}
                                  {hasMultipleTypes && (
                                    <span className="ml-1 text-muted-foreground">
                                      ({propertyTypes.length}+)
                                    </span>
                                  )}
                                </Badge>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {property.baseProperty?.latitude &&
                            property.baseProperty?.longitude ? (
                              <SmallMap
                                lat={property.baseProperty.latitude}
                                lng={property.baseProperty.longitude}
                                propertyName={
                                  property.baseProperty.property_name
                                }
                                onClick={() => handleMapClick(property)}
                              />
                            ) : (
                              <div className="w-14 h-10 bg-muted rounded border flex items-center justify-center">
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              {property.baseProperty?.latitude &&
                              property.baseProperty?.longitude ? (
                                <>
                                  <div>
                                    {property.baseProperty.latitude.toFixed(4)}°
                                  </div>
                                  <div>
                                    {property.baseProperty.longitude.toFixed(4)}
                                    °
                                  </div>
                                </>
                              ) : (
                                <div>No location</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {property.baseProperty?.featured && (
                              <Badge variant="default" className="text-xs">
                                <Star className="h-3 w-3 mr-1" />
                                Featured
                              </Badge>
                            )}
                            {property.baseProperty?.draft && (
                              <Badge variant="secondary" className="text-xs">
                                Draft
                              </Badge>
                            )}
                            {!property.baseProperty?.draft &&
                              !property.baseProperty?.hidden && (
                                <Badge variant="default" className="text-xs">
                                  Published
                                </Badge>
                              )}
                            {property.baseProperty?.hidden && (
                              <Badge variant="destructive" className="text-xs">
                                Hidden
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3 shrink-0" />
                              {formatDate(property.baseProperty?.created_at)}
                            </div>
                            {property.baseProperty?.created_by && (
                              <div
                                className="flex items-center gap-1 text-xs text-muted-foreground"
                                title={`Created by: ${property.baseProperty.created_by}`}
                              >
                                <User className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                  {formatCreator(
                                    property.baseProperty.created_by,
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(property)}
                              title="View property details"
                              className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(property)}
                              title="Edit property"
                              className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                                >
                                  <MoreHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDuplicate(
                                      property.baseProperty?._id,
                                      property.baseProperty?.slug,
                                    )
                                  }
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleGenerateSubtitle(
                                      property.baseProperty?._id,
                                    )
                                  }
                                  disabled={!!property.baseProperty?.subtitle}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {property.baseProperty?.subtitle
                                    ? "Subtitle Already Generated"
                                    : "Generate Subtitle"}
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleToggleDraft(
                                      property.baseProperty?._id,
                                      property.baseProperty?.draft || false,
                                    )
                                  }
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {property.baseProperty?.draft
                                    ? "Publish"
                                    : "Move to Draft"}
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleToggleHidden(
                                      property.baseProperty?._id,
                                      property.baseProperty?.hidden || false,
                                    )
                                  }
                                >
                                  <EyeOff className="h-4 w-4 mr-2" />
                                  {property.baseProperty?.hidden
                                    ? "Unhide"
                                    : "Hide"}
                                </DropdownMenuItem>

                                {(() => {
                                  const baseProperty = property.baseProperty;
                                  const propertyTags = resolvePropertyTags(
                                    baseProperty.tags || [],
                                    tags,
                                  );
                                  const hasSoldTag = propertyTags.some(
                                    (tag) => tag.tag_name === SOLD_TAG_NAME,
                                  );
                                  const hasRentedTag = propertyTags.some(
                                    (tag) => tag.tag_name === RENTED_TAG_NAME,
                                  );
                                  const isUpdatingStatusTag =
                                    pendingStatusTagPropertyId ===
                                    baseProperty._id;

                                  return (
                                    <>
                                      <DropdownMenuItem
                                        disabled={isUpdatingStatusTag}
                                        onClick={(event) =>
                                          handleToggleStatusTag(
                                            "sold",
                                            {
                                              id: baseProperty._id,
                                              name: baseProperty.property_name,
                                              hasTag: hasSoldTag,
                                            },
                                            event,
                                          )
                                        }
                                      >
                                        <Tag className="h-4 w-4 mr-2" />
                                        {hasSoldTag
                                          ? "Remove SOLD! Tag"
                                          : "Add SOLD! Tag"}
                                      </DropdownMenuItem>

                                      {(hasRentedTag ||
                                        isRentalListing(
                                          property.propertyTypes,
                                        )) && (
                                        <DropdownMenuItem
                                          disabled={isUpdatingStatusTag}
                                          onClick={(event) =>
                                            handleToggleStatusTag(
                                              "rented",
                                              {
                                                id: baseProperty._id,
                                                name: baseProperty.property_name,
                                                hasTag: hasRentedTag,
                                              },
                                              event,
                                            )
                                          }
                                        >
                                          <KeyRound className="h-4 w-4 mr-2" />
                                          {hasRentedTag
                                            ? "Remove Rented Tag"
                                            : "Add Rented Tag"}
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  );
                                })()}

                                <DropdownMenuSeparator />

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleToggleFeatured(
                                      property.baseProperty?._id,
                                      property.baseProperty?.featured || false,
                                    )
                                  }
                                >
                                  {property.baseProperty?.featured ? (
                                    <StarOff className="h-4 w-4 mr-2" />
                                  ) : (
                                    <Star className="h-4 w-4 mr-2" />
                                  )}
                                  {property.baseProperty?.featured
                                    ? "Remove from Featured"
                                    : "Add to Featured"}
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDownloadImages(
                                      property.baseProperty?._id,
                                      property.baseProperty?.reff_id,
                                    )
                                  }
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download Images
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />

                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDelete(property.baseProperty?._id)
                                  }
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* pagination controls */}
          {paginatedProperties.length > 0 && paginationInfo.totalPages > 1 && (
            <div className="mt-2">
              <Pagination>
                <PaginationContent className="gap-1">
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={handlePrevious}
                      className={
                        !paginationInfo.canGoPrevious
                          ? "pointer-events-none opacity-50 h-7"
                          : "cursor-pointer h-7"
                      }
                    />
                  </PaginationItem>

                  {generatePageNumbers().map((page, index) => (
                    <PaginationItem key={index}>
                      {page === "ellipsis" ? (
                        <PaginationEllipsis className="h-7" />
                      ) : (
                        <PaginationLink
                          onClick={() => handlePageChange(page as number)}
                          isActive={currentPage === page}
                          className="cursor-pointer h-7 w-7 text-xs"
                        >
                          {page}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      onClick={handleNext}
                      className={
                        !paginationInfo.canGoNext
                          ? "pointer-events-none opacity-50 h-7"
                          : "cursor-pointer h-7"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>

              <div className="text-center mt-1.5 text-xs text-muted-foreground">
                Page {currentPage} of {paginationInfo.totalPages}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* property dialog */}
      {dialogMode && (
        <PropertyFormWrapper
          mode={dialogMode}
          property={selectedProperty}
          open={!!dialogMode}
          onClose={handleCloseDialog}
          propertyId={selectedProperty?.baseProperty?._id}
        />
      )}

      <PropertyDetailsDialog
        open={viewDetailsOpen}
        onOpenChange={setViewDetailsOpen}
        property={viewProperty}
        propertyTypes={propertyTypesForView}
        typeDataMap={typeDataMap}
        singleTypeData={singleTypeData}
        isDetailedQueryLoading={isDetailedQueryLoading}
        tags={tags as PropertyTag[] | undefined}
        formatPropertyType={formatPropertyType}
        formatDate={formatDate}
        formatCreator={formatCreator}
      />

      {/* expanded map dialog */}
      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedMapProperty?.baseProperty?.property_name ||
                "Property Location"}
            </DialogTitle>
          </DialogHeader>
          {selectedMapProperty?.baseProperty?.latitude &&
            selectedMapProperty?.baseProperty?.longitude && (
              <div className="h-[500px] w-full rounded-lg overflow-hidden border relative z-0">
                <MapContainer
                  center={
                    [
                      selectedMapProperty.baseProperty.latitude,
                      selectedMapProperty.baseProperty.longitude,
                    ] as [number, number]
                  }
                  zoom={15}
                  style={{ width: "100%", height: "100%", zIndex: 0 }}
                  className="z-0"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker
                    position={
                      [
                        selectedMapProperty.baseProperty.latitude,
                        selectedMapProperty.baseProperty.longitude,
                      ] as [number, number]
                    }
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-semibold text-sm">
                          {selectedMapProperty.baseProperty.property_name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedMapProperty.baseProperty.reff_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedMapProperty.baseProperty.latitude.toFixed(6)}
                          °,{" "}
                          {selectedMapProperty.baseProperty.longitude.toFixed(
                            6,
                          )}
                          °
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
